import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { api } from './api.js';
import { authState, initAuth, subscribeToAuth } from './auth.js';

/**
 * The current sign-in state, kept in the auth module rather than in React so a
 * token refresh or a 401 on any request reaches every screen at once.
 */
export function useAuth() {
  useEffect(() => { initAuth(); }, []);
  return useSyncExternalStore(subscribeToAuth, authState, authState);
}

/**
 * Fetch a path, re-fetching when it changes. Returns a `reload` so screens can
 * refresh after a mutation without remounting.
 */
export function useApi(path, { skip = false } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(!skip);
  // Guards against a slow earlier request overwriting a newer one's result.
  const requestId = useRef(0);

  const load = useCallback(async () => {
    if (skip || !path) { setLoading(false); return; }
    const id = ++requestId.current;
    setLoading(true);
    try {
      const result = await api.get(path);
      if (id === requestId.current) { setData(result); setError(null); }
    } catch (err) {
      if (id === requestId.current) setError(err);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [path, skip]);

  useEffect(() => { load(); }, [load]);

  return { data, error, loading, reload: load, setData };
}

/** Track an async action's in-flight and error state for a button. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(async (fn) => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(err);
      return undefined;
    } finally {
      setBusy(false);
    }
  }, []);

  return { busy, error, setError, run };
}

export function useDebounced(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}
